import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiSolidMinusCircle, BiSolidPlusCircle } from 'react-icons/bi';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import CopyVariable from '../../../../common/CopyVariable';
import HintBlock from '../../../../common/HintBlock';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { CustomOptions } from '../text-scenario/scenario-lead-form.component';
import VideoScenarioDeleteButton from './video-scenario-delete-button';
import ScenarioSide from './video-scenario-side';

interface IFormData extends INoteForm {
  title: string;
  leads: Leads[];
  submitText?: string;
}

interface Leads {
  oldLabel?: string;
  newLabel: string;
  labelType: string;
  isRequired: boolean;
  isDeleted: boolean;
  options?: {
    name: string;
    value: string;
    isDefault: boolean;
  }[];
}

export const MacBotLeadForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);
  const [isDelete, setIsDelete] = useState(true);

  const schema = Yup.object({
    title: Yup.string().trim().max(31).required(t('MacBotLeadForm/scenario_name_required')),

    leads: Yup.array().of(
      Yup.object().shape({
        oldLabel: Yup.string().trim().optional().nullable(),

        newLabel: Yup.string()
          .trim()
          .nullable()
          .when('oldLabel', {
            is: (value: any) => !value,
            then: schema => schema.required(t('MacBotLeadForm/scenario_lead_label_required')),
            otherwise: schema => schema.notRequired(),
          }),

        labelType: Yup.string().trim().required(t('MacBotLeadForm/scenario_lead_type_required')),

        isRequired: Yup.boolean().required(t('MacBotLeadForm/scenario_lead_isRequired_required')),

        options: Yup.array()
          .of(
            Yup.object().shape({
              name: Yup.string().trim().required(t('MacBotLeadForm/scenario_lead_label_required')),
              value: Yup.string().trim().required(t('MacBotLeadForm/scenario_lead_label_required')),
              isDefault: Yup.boolean().optional().nullable(),
            })
          )
          .nullable()
          .when('labelType', {
            is: (val: string) => val === 'option', // <-- make sure this matches your actual value
            then: schema =>
              schema.min(1, t('MacBotLeadForm/scenario_lead_isRequired_required')).required(t('MacBotLeadForm/scenario_lead_isRequired_required')),
            otherwise: schema => schema.strip(), // <-- This completely removes `options` from validation context
          }),
      })
    ),

    submitText: Yup.string().trim().max(30).optional().nullable(),

    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors, isSubmitted },
    reset,
    handleSubmit,
    control,
    getFieldState,
    setValue,
    watch,
    getValues,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: itemData?.title ?? '',
      leads:
        itemData?.leads && itemData?.leads.length > 0
          ? itemData?.leads.map((lead: Leads) => {
              return {
                oldLabel: lead?.newLabel ?? '',
                newLabel: lead?.newLabel ?? '',
                labelType: lead?.labelType ?? '',
                isRequired: lead?.isRequired ?? false,
              };
            })
          : [
              {
                newLabel: '',
                labelType: '',
                isRequired: false,
                isDeleted: false,
              },
            ],
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
      submitText: itemData?.submitText ?? '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'leads',
  });
  const { fields: noteFields } = useFieldArray({
    control,
    name: 'notes',
  });
  const types = [
    { key: t('MacBotLeadForm/key8'), value: 'alphanumeric' },
    { key: t('MacBotLeadForm/key20'), value: 'multiline' },
    { key: t('MacBotLeadForm/key9'), value: 'numeric' },
    { key: t('MacBotLeadForm/key10'), value: 'email' },
    { key: t('MacBotLeadForm/key11'), value: 'date' },
    { key: t('MacBotLeadForm/key12'), value: 'dateTime' },
    { key: t('MacBotLeadForm/key13'), value: 'select' },
    { key: t('MacBotLeadForm/key17'), value: 'hidden' },
    { key: t('MacBotLeadForm/key18'), value: 'option' },
  ];

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const markAsDeleted = (index: number) => {
    const leadsClone = watch('leads');

    leadsClone[index].isDeleted = true;

    setValue('leads', leadsClone);
  };

  const onSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        title: data.title,
        submitText: data.submitText || null,
        type: BlockType.Lead,
        parent: itemData.parent,
        leads: data.leads.map((obj: Leads) => {
          return Object.fromEntries(Object.entries(obj).filter(([key, value]) => value !== null && value !== undefined && value !== ''));
        }),
        notes: data.notes,
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.Lead,
        title: data.title,
        submitText: data.submitText || null,
        parent: parentId,
        leads: data.leads.map((obj: Leads) => {
          return Object.fromEntries(Object.entries(obj).filter(([key, value]) => value !== null && value !== undefined && value !== ''));
        }),
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? '',
        submitText: itemData?.submitText,
        leads:
          itemData?.leads && itemData?.leads.length > 0
            ? itemData?.leads.map((lead: Leads) => {
                return {
                  oldLabel: lead?.newLabel ?? '',
                  newLabel: lead?.newLabel ?? '',
                  labelType: lead?.labelType ?? '',
                  isRequired: lead?.isRequired ?? false,
                };
              })
            : [
                {
                  newLabel: '',
                  labelType: '',
                  isRequired: false,
                  isDeleted: false,
                },
              ],
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowLeadForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotLeadForm/key2')}
                </Form.Label>
                <Form.Control
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  type="text"
                  max={30}
                  maxLength={30}
                  isInvalid={!!errors.title}
                  placeholder={''}
                  {...register('title')}
                />
                <HintBlock texts={['MacBotLeadForm/maxlenght']} />
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.title?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row>
            <Col xs={12}>
              {fields.map((field, index) => {
                if (!field.isDeleted)
                  return (
                    <div key={field.id} className={`tw-mt-2 tw-items-start ${index + 1 < fields.length ? 'mb-3' : ''} `}>
                      <div className="tw-flex tw-justify-between tw-items-center tw-mt-2 mb-4 g-md-3 md:tw-gap-y-0 tw-gap-y-3">
                        <div className="tw-flex-col tw-flex tw-justify-between tw-gap-3 tw-w-full">
                          <Form.Control hidden {...register(`leads.${index}.oldLabel`)} />

                          <Col xs={12}>
                            <Form.Group>
                              <Form.Control
                                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                                placeholder={t('MacBotLeadForm/key16').toString()}
                                isInvalid={!!errors.leads?.[index]?.newLabel}
                                {...register(`leads.${index}.newLabel`)}
                              />
                              <HintBlock texts={['MacBotLeadForm/formFieldMaxlenght']} />
                              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                                {errors.leads?.[index]?.newLabel?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col xs={12}>
                            <Form.Group>
                              <Form.Select
                                translate="no"
                                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                                isInvalid={!!errors.leads?.[index]?.labelType}
                                {...register(`leads.${index}.labelType`)}
                              >
                                <option value={''}>{t('MacBotLeadForm/key14')}</option>
                                {types.map(item => (
                                  <option key={item.key} value={item.value} className="tw-capitalize">
                                    {item.key}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                                {errors.leads?.[index]?.labelType?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                            {watch(`leads.${index}.labelType`) == 'option' && (
                              <div className="tw-w-full">
                                <Controller
                                  name={`leads.${index}.options`}
                                  control={control}
                                  defaultValue={[
                                    {
                                      name: 'defult',
                                      isDefault: true,
                                      value: '',
                                    },
                                  ]}
                                  rules={{
                                    validate: options => {
                                      if (!options || options.length === 0) {
                                        return 'At least one option is required.';
                                      }
                                      if (options.some(opt => opt.name.trim() === '')) {
                                        return 'Options cannot be empty.';
                                      }
                                      return true;
                                    },
                                  }}
                                  render={({ field, fieldState: { error } }) => (
                                    <CustomOptions
                                      formLeadIndex={index}
                                      value={field.value}
                                      onChange={(formLeadIndex, list) => {
                                        field.onChange(list);
                                      }}
                                      error={getFieldState(`leads.${index}.options`).error?.message} // Pass error message to CustomOptions
                                      isSubmited={isSubmitted}
                                    />
                                  )}
                                />
                              </div>
                            )}
                          </Col>
                          <Col xs={12}>
                            <Form.Group className="tw-flex tw-items-center tw-justify-between tw-h-12 tw-pl-4 tw-pr-3 tw-py-3 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-300 ">
                              <div>
                                <Form.Label htmlFor="" className="tw-text-[#11181c] tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-0">
                                  {t('MacBotLeadForm/key6')}
                                </Form.Label>
                                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                                  {errors.leads?.[index]?.isRequired?.message}
                                </Form.Control.Feedback>
                              </div>
                              <Form.Switch className="tw-p-0" {...register(`leads.${index}.isRequired`)} />
                            </Form.Group>
                          </Col>
                          <Col xs={12}>
                            <div className="tw-mt-3 tw-self-stretch tw-justify-start  tw-items-start tw-gap-2 tw-flex tw-flex-col ">
                              <CopyVariable title={watch('title')} text={watch(`leads.${index}.newLabel`)} />
                            </div>
                          </Col>
                        </div>

                        {fields.filter(f => !f.isDeleted).length > 1 && (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => markAsDeleted(index)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                markAsDeleted(index);
                              }
                            }}
                            className="tw-cursor-pointer tw-self-stretch tw-mx-[10px] tw-p-2.5 tw-bg-white tw-rounded-md tw-shadow border tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                          >
                            <div className="tw-flex tw-flex-col tw-gap-2 md:tw-pt-8">
                              <div className="tw-text-gray-400 tw-border-none tw-w-max tw-bg-transparent">
                                <BiSolidMinusCircle className="tw-text-red-400" size={20} />{' '}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="h-[0px] border border-gray-200 tw-my-4"></div>
                      </div>
                    </div>
                  );
              })}
            </Col>

            <Col xs={12}>
              <PSButton
                id="btn-lead-append"
                variant="outline"
                type="button"
                className="tw-w-full"
                onClick={() =>
                  append({
                    newLabel: '',
                    labelType: '',
                    isRequired: false,
                    isDeleted: false,
                  })
                }
                icon={<BiSolidPlusCircle className="tw-text-gray-400" />}
                title={t('MacBotLeadForm/key7')}
              />
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row>
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotLeadForm/key21')}
                </Form.Label>
                <Form.Control
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  type="text"
                  max={30}
                  maxLength={30}
                  isInvalid={!!errors.submitText}
                  placeholder={'Submit'}
                  {...register('submitText')}
                />
                <HintBlock texts={['MacBotLeadForm/maxlenght']} />
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.submitText?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-lead-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields: noteFields }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
